import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Eye, FileText, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSuperAdminCompany } from '../contexts/SuperAdminCompanyContext';
import CompanyCell from '../components/CompanyCell';

interface Sale {
  id: number;
  number: string;
  clientId: number;
  tenantId: number | null;
  items: SaleItem[];
  client: {
    id: number;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface SaleItem {
  id: number;
  saleId: number;
  qualityId: number;
  meters: number;
  quality: {
    id: number;
    name: string;
  };
}

const Sales: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deletingSale, setDeletingSale] = useState<Sale | null>(null);
  const [viewingSale, setViewingSale] = useState<Sale | null>(null);
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const { scope } = useSuperAdminCompany();
  const showCompanyColumn = user?.role === 'SUPERADMIN' && scope.mode === 'all';

  // Fetch sales
  const { data: salesResponse, isLoading, error } = useQuery({
    queryKey: ['sales'],
    queryFn: () => apiService.get('/sales'),
  });
  
  const sales = salesResponse?.sales || [];

  // Delete sale mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/sales/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['yarn-stock'] });
      toast.success('Sale deleted successfully');
      setDeletingSale(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete sale');
    },
  });

  // Generate PDF mutation
  const generatePDFMutation = useMutation({
    mutationFn: (id: number) => apiService.get(`/sales/${id}/pdf`),
    onSuccess: (response) => {
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sale-${response.data.saleNumber || 'chalan'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF generated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to generate PDF');
    },
  });

  const handleDelete = (sale: Sale) => {
    setDeletingSale(sale);
  };

  const handleView = (sale: Sale) => {
    setViewingSale(sale);
  };

  const handleGeneratePDF = (sale: Sale) => {
    generatePDFMutation.mutate(sale.id);
  };

  const confirmDelete = () => {
    if (deletingSale) {
      deleteMutation.mutate(deletingSale.id);
    }
  };

  const openCreateSale = () => {
    navigate('/sales/create');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load sales. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Management</h1>
          <p className="text-gray-600">Manage your sales and track orders</p>
        </div>
        <button
          type="button"
          onClick={openCreateSale}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Sale
        </button>
      </div>

      {/* Sales Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {sales.length === 0 ? (
          <div className="text-center py-12">
            <Truck className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg">No sales found</p>
            <p className="text-gray-400 text-sm mb-6">Create your first sale to get started</p>
            <button
              onClick={openCreateSale}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Sale
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {sales.map((sale: Sale) => (
              <li key={sale.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Truck className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">Sale #{sale.number}</div>
                      <div className="text-sm text-gray-500">
                        Client: {sale.client.name}
                      </div>
                      {showCompanyColumn && (
                        <div className="text-sm text-gray-500">
                          Company: <CompanyCell tenantId={sale.tenantId} />
                        </div>
                      )}
                      <div className="text-sm text-gray-500">
                        {sale.items.length} items • Created {new Date(sale.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleGeneratePDF(sale)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Generate PDF"
                      disabled={generatePDFMutation.isPending}
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleView(sale)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(sale)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Sale"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* View Sale Modal */}
      {viewingSale && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Sale #{viewingSale.number}
              </h3>
              <div className="space-y-4">
                <div className="text-sm text-gray-500">
                  Client: {viewingSale.client.name}
                </div>
                <div className="text-sm text-gray-500">
                  Created: {new Date(viewingSale.createdAt).toLocaleString()}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quality
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Meters
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {viewingSale.items.map((item: SaleItem) => (
                        <tr key={item.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.quality.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.meters.toFixed(2)}m
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 mt-6">
                <button
                  onClick={() => setViewingSale(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingSale && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Sale</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete Sale #{deletingSale.number}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingSale(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;